<template>
  <div class="w-full h-full pt-5">
    <div class="flex items-center px-4">
      <back-button
        label="Back"
        variant="secondary"
        @onClick="$router.back()"
        class="mr-3"
      />
      <div class="divider-line mr-3"></div>
      <h1 class="text-xl text-left font-extrabold text-darkPurple mr-4">
        Scheme of Service
      </h1>
      <Breadcrumb :items="breadcrumbs" />
    </div>

    <div class="mx-3 mt-5">
      <Card class="p-4" :footer-has-border="false" style="border-radius: 16px;">
        <div>
          <Tabs
            class="mx-4"
            :tabs="tabsItems"
            :active-tab="activeTab"
            @currentTab="onTabChange($event)"
            :border="true"
          />
        </div>

        <div class="flex justify-end pt-4">
          <Button
            background-color="none"
            class="text-dynamicBackBtn border border-solid border-dynamicBackBtn"
            @click="handleImport"
          >
            Import
          </Button>
        </div>

        <CardFooter
          class="mt-4 scheme-footer"
          :show-sort-filter="true"
          :show-search="true"
          :showfilter="true"
          showcalendar
          search-placeholder="Search"
          reloadcard
          @searchResult="handleSearch"
          @sortType="handleSort"
          @calendar="handleCalendar"
          @reload="handleReload"
        />
      </Card>
    </div>

    <div v-if="!showError" class="w-full h-full p-3">
      <NoOrgRegister
        v-if="!loadingData && !schemeRows.length"
        title="No scheme of service entries have been created yet."
        subtitle="Once entries are added, they will automatically appear in this register."
      />
      <sTable
        v-else
        :headers="schemeHeaders"
        :items="schemeRows"
        :pagination-list="metaData"
        :loading="loadingData"
        aria-label="scheme of service table"
        class="w-full"
        has-number
        page-sync
        @page="handlePage($event)"
        @itemsPerPage="handleItemsPerPage($event)"
      >
        <template v-slot:item="{ item }">
          <span v-if="item.id">
            <Menu left top="-150" margin="24" class="my-2 p-0">
              <template v-slot:title>
                <icon icon-name="more_icon" size="xs" />
              </template>
              <div style="width: 116px; height: 81px" class="py-3 px-2">
                <div
                  class="flex py-1 px-3 more h-8 cursor-pointer"
                  @click="handleEditScheme(item.data)"
                >
                  <icon
                    icon-name="edit"
                    class-name="text-blueCrayola mr-2"
                    size="xsm"
                  />
                  <p class="pt-1">Edit</p>
                </div>
                <div
                  class="flex px-3 py-1 more h-8 cursor-pointer"
                  @click="openDeleteSchemeDialog(item.data)"
                >
                  <icon
                    icon-name="delete"
                    class-name="text-desire mr-2"
                    size="xsm"
                  />
                  <p class="pt-1">Delete</p>
                </div>
              </div>
            </Menu>
          </span>
        </template>
      </sTable>
    </div>
    <div v-else>
      <error-page />
    </div>

    <SchemeServiceDrawer
      :is-open="showSchemeDrawer"
      :is-edit="Boolean(selectedScheme && selectedScheme.id)"
      :initial-data="selectedScheme"
      @close="closeDrawer"
      @save="handleSaveScheme"
    />

    <Modal v-if="dialogDeleteScheme">
      <Card class="p-5 flex flex-col max-w-md">
        <div>
          <p class="font-bold ml-4 my-5 text-center">
            <template v-if="pendingDeleteSchemeLabel">
              Are you sure you want to delete "{{ pendingDeleteSchemeLabel }}"?
            </template>
            <template v-else>
              Are you sure you want to delete this scheme of service entry?
            </template>
          </p>
          <div class="flex">
            <Button
              class="text-white"
              :background="appearance.buttonColor || defaultBTNColor"
              :disabled="deletingScheme"
              @click="confirmDeleteScheme"
            >
              Yes
            </Button>
            <Button
              background-color="none"
              class="text-dynamicBackBtn border border-solid border-dynamicBackBtn ml-4"
              @click="closeDeleteSchemeDialog"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </Modal>

    <import-employee
      :open-modal="isOpenImport"
      :is-overwrite-employee="false"
      :is-template="true"
      :url="uploadSchemeOfServiceUrl"
      @close="isOpenImport = false"
      @download="downloadSchemeOfServiceTemplate"
      @reload-employees="handleReload"
      @importEmployee="importSchemeOfService"
      :title="`Import Scheme of Service Data`"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import Tabs from "@scelloo/cloudenly-ui/src/components/tab";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import Button from "@/components/Button";
import Card from "@/components/Card";
import CardFooter from "@/components/CardFooter";
import Icon from "@/components/Icon";
import Menu from "@/components/Menu";
import Modal from "@/components/Modal";
import ImportEmployee from "@/components/importEmployee";
import ErrorPage from "@/modules/Admin/error403";
import NoOrgRegister from "@/modules/Admin/OrganisationModule/NoOrgRegister";
import SchemeServiceDrawer from "./SchemeServiceDrawer";

export default {
  name: "SchemeService",
  components: {
    BackButton,
    Breadcrumb,
    Tabs,
    STable,
    Button,
    Card,
    CardFooter,
    Icon,
    Menu,
    Modal,
    ImportEmployee,
    NoOrgRegister,
    SchemeServiceDrawer,
    ErrorPage,
  },
  data() {
    return {
      showError: false,
      showSchemeDrawer: false,
      selectedScheme: null,
      dialogDeleteScheme: false,
      pendingDeleteSchemeId: null,
      pendingDeleteSchemeLabel: "",
      deletingScheme: false,
      isOpenImport: false,
      uploadSchemeOfServiceUrl: `${process.env.VUE_APP_UPLOAD_CLOUDENLY_FILES}/document-management/upload/scheme-of-service`,
      loadingData: true,
      metaData: {
        total: 0,
        page: 1,
        lastPage: 1,
        nextPage: null,
        prevPage: null,
        from: 0,
        to: 0,
      },
      searchWord: null,
      sortType: null,
      numberOfPage: 1,
      itemsPerPage: 20,
      activeTab: "Academic",
      tabsItems: ["Academic", "Non-Academic"],
      breadcrumbs: [
        {
          disabled: false,
          text: "Organisation Design",
          id: "",
        },
        {
          disabled: false,
          text: "Scheme of Service",
          id: "Scheme of Service",
        },
      ],
      schemeRows: [],
    };
  },
  computed: {
    schemeHeaders() {
      return [
        { title: "JOB CADRES", value: "jobCadre" },
        { title: "POST", value: "post" },
        { title: "DUTIES", value: "duties" },
        { title: "PAY SCALE", value: "payScale" },
        { title: "BASIC ENTRY QUALIFICATION", value: "qualification" },
        { title: "NEXT GRADE", value: "nextGrade" },
        { title: "", value: "id", image: true },
      ];
    },
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),
  },
  methods: {
    queryUpdate() {
      const search = this.searchWord ? `&search=${this.searchWord}` : "";
      const sort = this.sortType ? `&sort=${this.sortType}` : "";
      const pageNumber = this.numberOfPage ? `&page=${this.numberOfPage}` : "";
      const itemPage = this.itemsPerPage ? `&perPage=${this.itemsPerPage}` : "";
      const isAcademic = `&isAcademic=${this.activeTab === "Academic"}`;
      this.getSchemeOfService(
        `${sort}${search}${pageNumber}${itemPage}${isAcademic}`
      );
    },
    async getSchemeOfService(payload = "") {
      this.loadingData = true;
      try {
        const response = await this.$_getSchemeOfService(payload);
        const data = response?.data || {};
        const rows = data.schemes || [];
        this.schemeRows = rows.map((item) => ({
          id: item.id,
          jobCadre: item.schemeJobClassification?.name ?? "------",
          post: item.schemeDesignation?.name ?? "------",
          duties: item.duties ?? "------",
          payScale: item.payScale ?? "------",
          qualification: item.basicEntryQualification ?? "------",
          nextGrade: item.nextGrade ?? "------",
          jobClassificationId: item.schemeJobClassification?.id ?? null,
          designationId: item.schemeDesignation?.id ?? null,
          basicEntryQualification: item.basicEntryQualification ?? "",
          schemeJobClassification: item.schemeJobClassification,
          schemeDesignation: item.schemeDesignation,
          isAcademic: item.isAcademic,
        }));
        this.metaData = response?.data?.meta || {
          total: this.schemeRows.length,
          page: 1,
          lastPage: 1,
          nextPage: null,
          prevPage: null,
          from: this.schemeRows.length ? 1 : 0,
          to: this.schemeRows.length,
        };
      } catch (error) {
        this.schemeRows = [];
        this.metaData = {
          total: 0,
          page: 1,
          lastPage: 1,
          nextPage: null,
          prevPage: null,
          from: 0,
          to: 0,
        };
      } finally {
        this.loadingData = false;
      }
    },
    async importSchemeOfService(fileUrl, overwriteEmployee, { resolve, reject }) {
      try {
        const payload = {
          creator: this.$AuthUser.id,
          fileUrl,
        };
        const response = await this.$_importSchemeOfService(payload);
        resolve(response);
      } catch (error) {
        reject(error);
      }
    },
    async downloadSchemeOfServiceTemplate(){
      try {
        const response = await this.$_getSchemeOfServiceTemplate();
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = "Scheme of Service Template.xlsx";
        // Appended the link to the body (necessary for Firefox)
        document.body.appendChild(link);
        link.click();

        // Removed the link after download
        document.body.removeChild(link);
      } catch (error) {
        this.$toasted.error("Error downloading file", { duration: 6000 });
      }
    },
    onTabChange(tab) {
      this.activeTab = tab;
      this.numberOfPage = 1;
      this.queryUpdate();
    },
    handlePage(pageNumber) {
      this.numberOfPage = pageNumber;
      this.queryUpdate();
    },
    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.queryUpdate();
    },
    handleSearch(searchWord) {
      this.loadingData = true;
      if (searchWord) {
        this.searchWord = searchWord;
        this.queryUpdate();
      } else {
        this.searchWord = null;
        this.queryUpdate();
      }
    },
    handleSort(sortType) {
      this.loadingData = true;
      const changeSortType = sortType.split(":");
      let result;
      if (changeSortType[0] === "createdAt") {
        result = sortType;
      } else {
        result = `name:${changeSortType[1]}`;
      }
      this.sortType = result;
      this.queryUpdate();
    },
    handleCalendar() {},
    handleReload() {
      this.queryUpdate();
    },
    async handleImport() {
      try {
        await this.$handlePrivilege("schemeOfService", "importSchemeOfService");
        this.isOpenImport = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    closeDrawer() {
      this.showSchemeDrawer = false;
      this.selectedScheme = null;
    },
    async handleEditScheme(row) {
      if (!row?.id) {
        return;
      }
      try {
        await this.$handlePrivilege("schemeOfService", "editSchemeOfService");
        const emptyDisplay = (value) => (value === "------" ? "" : value || "");
        this.selectedScheme = {
          ...row,
          duties: emptyDisplay(row.duties),
          payScale: emptyDisplay(row.payScale),
          basicEntryQualification: emptyDisplay(
            row.basicEntryQualification
          ),
          nextGrade: emptyDisplay(row.nextGrade),
        };
        this.showSchemeDrawer = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async handleSaveScheme(payload) {
      if (payload.isEdit && payload.id) {
        await this.updateScheme(payload);
      }
    },
    buildSchemePayload(payload) {
      return {
        jobClassificationId: payload.jobClassificationId,
        designationId: payload.designationId,
        duties: payload.duties || "",
        basicEntryQualification: payload.basicEntryQualification || "",
        nextGrade: payload.nextGrade || "",
      };
    },
    async updateScheme(payload) {
      try {
        await this.$_updateSchemeOfService(
          payload.id,
          this.buildSchemePayload(payload)
        );
        this.$toasted.success("Scheme of service updated successfully", {
          duration: 5000,
        });
        this.queryUpdate();
        this.closeDrawer();
      } catch (error) {
        this.$toasted.error("Unable to update scheme of service", {
          duration: 5000,
        });
      }
    },
    async openDeleteSchemeDialog(row) {
      if (!row?.id) {
        return;
      }
      try {
        await this.$handlePrivilege("schemeOfService", "deleteSchemeOfService");
        this.pendingDeleteSchemeId = row.id;
        const cadre = row.jobCadre && row.jobCadre !== "------" ? row.jobCadre : "";
        const post = row.post && row.post !== "------" ? row.post : "";
        this.pendingDeleteSchemeLabel = [cadre, post].filter(Boolean).join(" - ");
        this.dialogDeleteScheme = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    closeDeleteSchemeDialog() {
      this.dialogDeleteScheme = false;
      this.pendingDeleteSchemeId = null;
      this.pendingDeleteSchemeLabel = "";
    },
    async confirmDeleteScheme() {
      if (!this.pendingDeleteSchemeId || this.deletingScheme) {
        return;
      }
      this.deletingScheme = true;
      try {
        await this.$_deleteSchemeOfService(this.pendingDeleteSchemeId);
        this.$toasted.success("Scheme of service deleted successfully", {
          duration: 5000,
        });
        this.closeDeleteSchemeDialog();
        this.queryUpdate();
      } catch (error) {
        this.$toasted.error("Unable to delete scheme of service", {
          duration: 5000,
        });
      } finally {
        this.deletingScheme = false;
      }
    },
  },
  async mounted() {
    try {
      await this.$handlePrivilege("schemeOfService", "viewSchemeOfService");
      this.showError = false;
      this.queryUpdate();
    } catch (error) {
      this.showError = true;
      this.loadingData = false;
    }
  },
};
</script>

<style scoped>
.divider-line {
  border-left: 1px solid #878e99;
  height: 1.5rem;
}

.scheme-footer {
  border-top: 1px solid #0000001a;
}

.more:hover {
  background: #eeeeee;
}
</style>